import { useShopData } from 'hooks/useShopData';
import { useUserData } from 'hooks/useUserData';
import { useAppSelector } from 'redux/hooks';
import { currentEcomSelector } from 'redux/slices/ecoms-slice';
import { EcomResponse_MarketDrivenShipping } from '../../../../pb/responses/v1/ecom_response_pb';
import { selectEnabledShippingTypesSelector } from 'redux/slices/enabled-shipping-types/enabled-shipping-types.selector';
import { selectPackageProviders } from 'redux/slices/package-providers/package-providers.selector';
import { selectShopifyCurrentSubscription } from 'redux/slices/shopify-app-pricing/shopify-app-pricing.selector';
import { Feature } from '../../../../pb/subscription/v1/addon_pb';
import { DebugInfoGroup } from '../models/debug-info';

const toValue = (value: unknown): string | null => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'bigint') return value.toString();

    return String(value);
};

const toListValue = (values: string[]): string | null => (values.length ? values.join(', ') : null);

const useDebugInfo = (): DebugInfoGroup[] => {
    const ecom = useAppSelector(currentEcomSelector);
    const host = useAppSelector((state) => state.hostSlice?.host);
    const planLimits = useAppSelector((state) => state.planLimitsCount?.limits);
    const currentSubscription = useAppSelector(selectShopifyCurrentSubscription);
    const providers = useAppSelector(selectPackageProviders);
    const enabledShippingTypes = useAppSelector(selectEnabledShippingTypesSelector);

    const { data: userData } = useUserData();
    const { data: shopData } = useShopData();

    const identity: DebugInfoGroup = {
        title: 'Identity',
        rows: [
            { label: 'Ecom ID', value: toValue(ecom?.id) },
            { label: 'Ecom unique ID', value: toValue(ecom?.uniqueId) },
            { label: 'Ecom domain', value: toValue(ecom?.domain) },
            {
                label: 'Market driven shipping',
                value: toValue(ecom ? EcomResponse_MarketDrivenShipping[ecom.marketDrivenShipping] : undefined),
            },
            { label: 'Shop domain', value: toValue(shopify?.config?.shop) },
            { label: 'Host', value: toValue(host) },
            { label: 'User ID', value: toValue(userData?.id) },
            { label: 'User email', value: toValue(userData?.email) },
            { label: 'User name', value: toValue(userData?.name) },
        ],
    };

    const plan: DebugInfoGroup = {
        title: 'Plan & subscription',
        rows: [
            { label: 'Currency', value: toValue(shopData?.shop?.currency) },
            { label: 'Country', value: toValue(shopData?.shop?.country) },
            { label: 'Shopify plan', value: toValue(shopData?.shop?.plan_name) },
            { label: 'Shopify plan name', value: toValue(shopData?.shop_plan?.displayName) },
            { label: 'Partner development', value: toValue(shopData?.shop_plan?.partnerDevelopment) },
            { label: 'Shopify Plus', value: toValue(shopData?.shop_plan?.shopifyPlus) },
            { label: 'Checkout API supported', value: toValue(shopData?.shop_plan?.checkoutApiSupported) },
            { label: 'Subscription', value: toValue(currentSubscription?.name) },
            { label: 'Subscription status', value: toValue(currentSubscription?.status) },
            { label: 'Trial days', value: toValue(currentSubscription?.trialDays) },
            { label: 'Test subscription', value: toValue(currentSubscription?.test) },
            { label: 'Current period end', value: toValue(currentSubscription?.currentPeriodEnd) },
            ...(planLimits ?? []).map((limit) => ({
                label: Feature[limit.name] ?? `Feature ${limit.name}`,
                value: toValue(`${limit.currentlyUsed}/${limit.value}`),
            })),
        ],
    };

    const shipping: DebugInfoGroup = {
        title: 'Providers & shipping types',
        rows: [
            {
                label: 'Package providers',
                value: toListValue((providers ?? []).map((provider) => provider.name)),
            },
            ...(enabledShippingTypes ?? []).map((country) => ({
                label: `Shipping types (${country.countryCode})`,
                value: toListValue(
                    country.providers.map((provider) => `${provider.name}: ${provider.shippingTypes.join('|')}`)
                ),
            })),
        ],
    };

    return [identity, plan, shipping];
};

export default useDebugInfo;
